import { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { useMoveDeal, useCreateDeal } from '@/api/useDeals';
import { DealCard } from '@/components/Deals/DealCard';
import { DealStatusColumn } from '@/components/Deals/DealStatusColumn';
import { DEAL_STATUS_ORDER, DEAL_STATUS_COLORS } from '@/types/deal';
import type { Deal, DealStatus } from '@/types/deal';
import styles from './KanbanBoard.module.css';

interface KanbanBoardProps {
  deals: Deal[];
}

export const KanbanBoard = ({ deals }: KanbanBoardProps) => {
  const moveDeal = useMoveDeal();
  const createDeal = useCreateDeal();
  const [addingTo, setAddingTo] = useState<DealStatus | null>(null);
  const [newClient, setNewClient] = useState('');
  const [newInfo, setNewInfo] = useState('');

  const dealsByStatus = DEAL_STATUS_ORDER.reduce(
    (acc, status) => {
      acc[status] = deals
        .filter((d) => d.status === status)
        .sort((a, b) => a.position - b.position);
      return acc;
    },
    {} as Record<DealStatus, Deal[]>,
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const dealId = result.draggableId;
    const newStatus = result.destination.droppableId as DealStatus;
    const newPosition = result.destination.index;
    moveDeal.mutate({ id: dealId, data: { status: newStatus, position: newPosition } });
  };

  const handleAdd = (status: DealStatus) => {
    if (!newClient.trim()) return;
    createDeal.mutate(
      {
        title: newClient.trim(),
        client_name: newClient.trim(),
        notes: newInfo.trim(),
        status,
      },
      {
        onSuccess: () => {
          setAddingTo(null);
          setNewClient('');
          setNewInfo('');
        },
      },
    );
  };

  const handleCancel = () => {
    setAddingTo(null);
    setNewClient('');
    setNewInfo('');
  };

  const handleKeyDown = (e: React.KeyboardEvent, status: DealStatus) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd(status);
    }
    if (e.key === 'Escape') handleCancel();
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={styles.board}>
        {DEAL_STATUS_ORDER.map((status) => (
          <Droppable key={status} droppableId={status}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`${styles.column} ${snapshot.isDraggingOver ? styles.columnDragOver : ''}`}
                style={{ '--column-accent': DEAL_STATUS_COLORS[status] } as React.CSSProperties}
              >
                <DealStatusColumn status={status} count={dealsByStatus[status].length} />
                <div className={styles.cardList}>
                  {dealsByStatus[status].map((deal, index) => (
                    <Draggable key={deal.id} draggableId={String(deal.id)} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={dragSnapshot.isDragging ? styles.cardDragging : ''}
                        >
                          <DealCard deal={deal} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>

                {/* Inline add — Trello style */}
                {addingTo === status ? (
                  <div className={styles.addForm}>
                    <input
                      className={styles.addInput}
                      value={newClient}
                      onChange={(e) => setNewClient(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, status)}
                      placeholder="Company..."
                      autoFocus
                    />
                    <textarea
                      className={styles.addTextarea}
                      value={newInfo}
                      onChange={(e) => setNewInfo(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, status)}
                      placeholder="Deal info..."
                      rows={3}
                    />
                    <div className={styles.addActions}>
                      <button
                        className={styles.addSubmit}
                        onClick={() => handleAdd(status)}
                        disabled={!newClient.trim() || createDeal.isPending}
                      >
                        {createDeal.isPending ? 'Adding...' : 'Add'}
                      </button>
                      <button className={styles.addCancel} onClick={handleCancel}>
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className={styles.addButton}
                    onClick={() => {
                      setAddingTo(status);
                      setNewClient('');
                      setNewInfo('');
                    }}
                  >
                    + Add deal
                  </button>
                )}
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
};
